import { useState, useEffect } from 'react';
import { 
  Layout, Card, Typography, Tabs, Row, Col, Statistic, Tag, 
  Space, Avatar, Spin, Alert, List
} from 'antd';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import {
  CalendarOutlined, 
  ClockCircleOutlined, 
  UserOutlined, 
  BookOutlined, 
  TrophyOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  BarChartOutlined,
  PieChartOutlined,
  LineChartOutlined
} from '@ant-design/icons';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import getUserIdFromToken from '../../Utils/authUtils';
import { useTranslation } from 'react-i18next';
import './MyPerformance.css'

const { Content } = Layout;
const { Title, Text } = Typography;
const { TabPane } = Tabs;
const parseHoursToMinutes = (hours) => {
  if (!hours) return 0;
  
  // Handle formats like "2h 30m", "1h", "1.5", "90m", "1:30"
  const hourMinuteMatch = hours.match(/(\d+)h\s*(\d+)?m?/);
  const decimalMatch = hours.match(/^(\d+\.?\d*)$/);
  const minuteMatch = hours.match(/^(\d+)m$/);
  const timeMatch = hours.match(/^(\d+):(\d+)$/);
  
  if (hourMinuteMatch) {
    const h = parseInt(hourMinuteMatch[1], 10);
    const m = parseInt(hourMinuteMatch[2] || '0', 10);
    return h * 60 + m;
  } else if (decimalMatch) {
    return Math.round(parseFloat(decimalMatch[1]) * 60);
  } else if (minuteMatch) {
    return parseInt(minuteMatch[1], 10);
  } else if (timeMatch) {
    const h = parseInt(timeMatch[1], 10);
    const m = parseInt(timeMatch[2], 10);
    return h * 60 + m;
  }
  
  return 0;
};

const formatMinutesToHours = (totalMinutes) => {
  if (totalMinutes === 0) return '0h';
  
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  
  if (minutes === 0) {
    return `${hours}h`;
  } else {
    return `${hours}h ${minutes}m`;
  }
};

// 2. Calculate course statistics
const calculateCourseHours = (schedule) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time for accurate date comparison
  
  let totalMinutes = 0;
  let finishedMinutes = 0;
  let upcomingMinutes = 0;
  
  schedule?.forEach(cls => {
    const classMinutes = parseHoursToMinutes(cls.hours);
    totalMinutes += classMinutes;
    
    const classDate = new Date(cls.date);
    classDate.setHours(0, 0, 0, 0);
    
    if (classDate < today) {
      finishedMinutes += classMinutes;
    } else {
      upcomingMinutes += classMinutes;
    }
  });
  
  return {
    total: totalMinutes,
    finished: finishedMinutes,
    upcoming: upcomingMinutes
  };
};


const MyPerformance = () => {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState('overview');
  const [registrations, setRegistrations] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL;
  const userId = getUserIdFromToken();

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) {
        setError('User not authenticated');
        setLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        // Fetch registrations
        const registrationsResponse = await axios.get(`${API_URL}/registrations/user`, { headers });
        setRegistrations(registrationsResponse.data.registrations);

        // Fetch attendance data
        const attendanceResponse = await axios.get(`${API_URL}/attendance/student/${userId}`, { headers });
        setAttendance(attendanceResponse.data);

        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to fetch data');
        setLoading(false);
      }
    };

    fetchData();
  }, [userId, API_URL]);

  
    const getAttendanceForClass = (classId) => {
      return attendance.find((log) => log.classid === classId);
    };
  
  
  
    function calculateClassStats(registration) {
      if (!registration.schedule || !registration.schedule.length) {
        return { attendanceRate: 0, completionRate: 0 };
      }
  
      const schedule = registration.schedule;
      const totalClasses = schedule.length;
      
      const classesWithAttendance = schedule.map(cls => {
        const attendanceLog = getAttendanceForClass(cls.classid);
        return attendanceLog ? attendanceLog.status : 'No Attendance';
      });
  
      const presentClasses = classesWithAttendance.filter(status => status === 'Present').length;
      const lateClasses = classesWithAttendance.filter(status => status?.startsWith('Late')).length;
      
      return {
        attendanceRate: Math.round(((presentClasses + lateClasses) / totalClasses) * 100),
        completionRate: Math.round((presentClasses / totalClasses) * 100)
      };
    }
  
    const formatDate = (dateString) => {
      const date = new Date(dateString);
      const options = { day: '2-digit', month: 'short', year: 'numeric' };
      return new Intl.DateTimeFormat(i18n.language, options).format(date).toUpperCase();
    };
  
    const formatTime = (timeString) => {
      if (!timeString) return '';
      const [hours, minutes] = timeString.split(':');
      return `${hours}:${minutes}`;
    };
  
    const calculateEndTime = (startTime, duration) => {
      if (!startTime || !duration) return '';
      
      const [startHours, startMinutes] = startTime.split(':').map(Number);
      
      // Handle duration in HH:MM format
      let durationHours = 0;
      let durationMinutes = 0;
      
      if (duration.includes(':')) {
        [durationHours, durationMinutes] = duration.split(':').map(Number);
      } else {
        // If duration is just a number, treat as hours
        durationHours = parseFloat(duration);
        durationMinutes = (durationHours % 1) * 60;
        durationHours = Math.floor(durationHours);
      }
  
      let endHours = startHours + durationHours;
      let endMinutes = startMinutes + durationMinutes;
      
      if (endMinutes >= 60) {
        endHours += Math.floor(endMinutes / 60);
        endMinutes %= 60;
      }
      
      return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
    };
  
    const getStatusConfig = (status) => {
      const configs = {
        'Present': {
          color: 'success',
          text: t('finished') || 'Completed',
          icon: CheckCircleOutlined
        },
        'Late': {
          color: 'warning',
          text: t('late') || 'Late', 
          icon: ExclamationCircleOutlined
        },
        'Absent': {
          color: 'error',
          text: t('absent') || 'Absent',
          icon: CloseCircleOutlined
        },
        'No Attendance': {
          color: 'default',
          text: t('scheduled') || 'Scheduled',
          icon: ClockCircleOutlined
        }
      };
      return configs[status] || configs['No Attendance'];
    };
  
   const calculateCourseDetails = (registration) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    let totalMinutes = 0;
    let studiedMinutes = 0;
    let remainingMinutes = 0;
    let completedClasses = 0;
    let upcomingClasses = 0;
    let totalClasses = 0;
    let presentCount = 0;
    let lateCount = 0;
    let absentCount = 0;
    
    registration.schedule?.forEach(cls => {
      const classMinutes = parseHoursToMinutes(cls.hours);
      totalMinutes += classMinutes;
      totalClasses++;
      
      const classDate = new Date(cls.date);
      classDate.setHours(0, 0, 0, 0);
      
      const attendanceLog = getAttendanceForClass(cls.classid);
      const status = attendanceLog ? attendanceLog.status : 'No Attendance';
      
      if (classDate < today) {
        completedClasses++;
        // Count all past classes as studied hours regardless of attendance status
        studiedMinutes += classMinutes;
        
        // Count attendance for display purposes only
        if (status === 'Present') presentCount++;
        else if (status?.startsWith('Late')) lateCount++;
        else if (status === 'Absent') absentCount++;
      } else {
        upcomingClasses++;
        remainingMinutes += classMinutes;
      }
    });
    
    const attendanceRate = completedClasses > 0 ? Math.round(((presentCount + lateCount) / completedClasses) * 100) : 0;
    const completionRate = totalClasses > 0 ? Math.round((completedClasses / totalClasses) * 100) : 0;
    const studyEfficiency = completedClasses > 0 ? Math.round((presentCount / completedClasses) * 100) : 0;
    
    return {
      totalMinutes,
      studiedMinutes,
      remainingMinutes,
      completedClasses,
      upcomingClasses,
      totalClasses,
      presentCount,
      lateCount,
      absentCount,
      attendanceRate,
      completionRate,
      studyEfficiency
    };
  };
  
  // Calculate overall statistics
  const calculateOverallStats = () => {
    let totalCourses = registrations.length;
    let activeCourses = 0;
    let completedCourses = 0;
    let totalStudyMinutes = 0;
    let totalRemainingMinutes = 0;
    let totalScheduledMinutes = 0;
    let overallPresentCount = 0;
    let overallCompletedClasses = 0;
    
    registrations.forEach(reg => {
      const courseDetails = calculateCourseDetails(reg);
      totalScheduledMinutes += courseDetails.totalMinutes;
      totalStudyMinutes += courseDetails.studiedMinutes;
      totalRemainingMinutes += courseDetails.remainingMinutes;
      overallPresentCount += courseDetails.presentCount;
      overallCompletedClasses += courseDetails.completedClasses;
      
      if (courseDetails.upcomingClasses > 0) {
        activeCourses++;
      } else if (courseDetails.completedClasses === courseDetails.totalClasses) {
        completedCourses++;
      }
    });
    
    const overallStudyEfficiency = overallCompletedClasses > 0 ? Math.round((overallPresentCount / overallCompletedClasses) * 100) : 0;
    const overallProgress = totalScheduledMinutes > 0 ? Math.round((totalStudyMinutes / totalScheduledMinutes) * 100) : 0;
    
    return {
      totalCourses,
      activeCourses,
      completedCourses,
      totalStudyMinutes,
      totalRemainingMinutes,
      totalScheduledMinutes,
      overallStudyEfficiency,
      overallProgress
    };
  };
  
  const overallStats = calculateOverallStats();

  // Loading and error states
  if (loading) {
    return (
      <Layout style={{ minHeight: '100vh' }}>
        <Sidebar />
        <Layout>
          <Header />
          <Content style={{ padding: '50px', textAlign: 'center' }}>
            <Spin size="large" />
            <div style={{ marginTop: 20 }}>
              <Text>{t('Loading your performance data...')}</Text>
            </div>
          </Content>
        </Layout>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout style={{ minHeight: '100vh' }}>
        <Sidebar />
        <Layout>
          <Header />
          <Content style={{ padding: '50px' }}>
            <Alert
              message="Error Loading Data"
              description={error}
              type="error"
              showIcon
              style={{ maxWidth: 600, margin: '0 auto' }}
            />
          </Content>
        </Layout>
      </Layout>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sidebar />
      <Layout>
        <Header />
        <Content  className="performance-content">

          {/* Main Tabs */}
          <Card>
            <Tabs activeKey={activeTab} onChange={setActiveTab} size="large">
              
              {/* Overview Tab */}
                            <TabPane 
                tab={
                  <span>
                    {t('Overview') || 'Overview'}
                  </span>
                } 
                key="overview"
              >
                <Row gutter={[24, 24]}>

                  {/* Quick Summary Insights */}
                  <Col span={24}>
                    <Card 
                      title={
                        <Space>
                          <TrophyOutlined />
                          {t('Study Insights') || 'Study Insights'}
                        </Space>
                      }
                    >
                      <Row gutter={[16, 16]}>
                        <Col xs={24} md={8}>
                          <div style={{ textAlign: 'center', padding: '20px' }}>
                            <Text strong style={{ fontSize: '24px', color: '#52c41a' }}>
                              {overallStats.overallStudyEfficiency}%
                            </Text>
                            <br />
                            <Text type="secondary">Study Efficiency</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              Classes attended successfully
                            </Text>
                          </div>
                        </Col>
                        
                        <Col xs={24} md={8}>
                          <div style={{ textAlign: 'center', padding: '20px' }}>
                            <Text strong style={{ fontSize: '24px', color: '#1890ff' }}>
                              {formatMinutesToHours(overallStats.totalScheduledMinutes)}
                            </Text>
                            <br />
                            <Text type="secondary">Total Scheduled</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              Across all courses
                            </Text>
                          </div>
                        </Col>
                        
                        <Col xs={24} md={8}>
                          <div style={{ textAlign: 'center', padding: '20px' }}>
                            <Text strong style={{ fontSize: '24px', color: '#faad14' }}>
                              {overallStats.activeCourses}
                            </Text>
                            <br />
                            <Text type="secondary">Active Courses</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              Currently in progress
                            </Text>
                          </div>
                        </Col>
                      </Row>
                    </Card>
                  </Col>
                  {/* Overall Summary Cards */}
                  <Col span={24}>
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={12} md={6}>
                        <Card style={{ textAlign: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                          <Statistic
                            title={<span style={{ color: 'white' }}>{t('Active Courses') || 'Active Courses'}</span>}
                            value={overallStats.activeCourses}
                            suffix={`/ ${overallStats.totalCourses}`}
                            prefix={<BookOutlined style={{ color: 'white' }} />}
                            valueStyle={{ color: 'white' }}
                          />
                        </Card>
                      </Col>
              
                      <Col xs={24} sm={12} md={6}>
                        <Card style={{ textAlign: 'center', background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
                          <Statistic
                            title={<span style={{ color: 'white' }}>{t('Total Study Hours') || 'Total Study Hours'}</span>}
                            value={formatMinutesToHours(overallStats.totalStudyMinutes)}
                            prefix={<ClockCircleOutlined style={{ color: 'white' }} />}
                            valueStyle={{ color: 'white', fontSize: '24px' }}
                          />
                        </Card>
                      </Col>
              
                      <Col xs={24} sm={12} md={6}>
                        <Card style={{ textAlign: 'center', background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
                          <Statistic
                            title={<span style={{ color: 'white' }}>{t('Hours Remaining') || 'Hours Remaining'}</span>}
                            value={formatMinutesToHours(overallStats.totalRemainingMinutes)}
                            prefix={<CalendarOutlined style={{ color: 'white' }} />}
                            valueStyle={{ color: 'white', fontSize: '24px' }}
                          />
                        </Card>
                      </Col>
              
                      <Col xs={24} sm={12} md={6}>
                        <Card style={{ textAlign: 'center', background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
                          <Statistic
                            title={<span style={{ color: 'white' }}>{t('Study Progress') || 'Study Progress'}</span>}
                            value={overallStats.overallProgress}
                            suffix="%"
                            prefix={<TrophyOutlined style={{ color: 'white' }} />}
                            valueStyle={{ color: 'white' }}
                          />
                        </Card>
                      </Col>
                    </Row>
                  </Col>
              
                  {/* Course-by-Course Breakdown */}
                  <Col span={24}>
                    <Card 
                      title={
                        <Space>
                          <BookOutlined />
                          {t('Course Progress Details') || 'Course Progress Details'}
                        </Space>
                      }
                    >
                      <Row gutter={[16, 16]}>
                        {registrations.map((registration, index) => {
                          const courseDetails = calculateCourseDetails(registration);
                          
                          return (
                            <Col xs={24} lg={12} key={registration.groupid || index}>
                              <Card 
                                size="small" 
                                style={{ 
                                  border: '1px solid #e8e8e8',
                                  borderRadius: '12px',
                                  overflow: 'hidden'
                                }}
                                bodyStyle={{ padding: '16px' }}
                              >
                                {/* Course Header */}
                                <div style={{ marginBottom: '16px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div>
                                      <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
                                        {registration.class_code}
                                      </Text>
                                      <br />
                                      <Text type="secondary" style={{ fontSize: '12px' }}>
                                        {registration.subjects?.[0]?.title || 'No Subject'}
                                      </Text>
                                    </div>
                                    <Tag color={courseDetails.upcomingClasses > 0 ? 'processing' : 'success'}>
                                      {courseDetails.upcomingClasses > 0 ? 'Active' : 'Completed'}
                                    </Tag>
                                  </div>
                                </div>
              
                                {/* Hours Progress Bar */}
                                <div style={{ marginBottom: '16px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                    <Text strong>Study Progress</Text>
                                    <Text strong style={{ color: '#1890ff' }}>
                                      {formatMinutesToHours(courseDetails.studiedMinutes)} / {formatMinutesToHours(courseDetails.totalMinutes)}
                                    </Text>
                                  </div>
                                  
                                  <div style={{ 
                                    height: '8px', 
                                    backgroundColor: '#f0f0f0', 
                                    borderRadius: '4px',
                                    overflow: 'hidden',
                                    marginBottom: '8px'
                                  }}>
                                    <div style={{
                                      height: '100%',
                                      width: `${courseDetails.totalMinutes > 0 ? (courseDetails.studiedMinutes / courseDetails.totalMinutes) * 100 : 0}%`,
                                      backgroundColor: '#52c41a',
                                      transition: 'width 0.3s ease'
                                    }} />
                                  </div>
                                  
                                  <Text type="secondary" style={{ fontSize: '11px' }}>
                                    {courseDetails.totalMinutes > 0 ? Math.round((courseDetails.studiedMinutes / courseDetails.totalMinutes) * 100) : 0}% Complete • 
                                    {formatMinutesToHours(courseDetails.remainingMinutes)} remaining
                                  </Text>
                                </div>
              
                                {/* Stats Grid */}
                                <Row gutter={[8, 8]}>
                                  <Col xs={12}>
                                    <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#f6ffed', borderRadius: '6px' }}>
                                      <Text strong style={{ color: '#52c41a', fontSize: '18px' }}>
                                        {courseDetails.presentCount}
                                      </Text>
                                      <br />
                                      <Text type="secondary" style={{ fontSize: '11px' }}>Present</Text>
                                    </div>
                                  </Col>
                                  <Col xs={12}>
                                    <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#fff2f0', borderRadius: '6px' }}>
                                      <Text strong style={{ color: '#ff4d4f', fontSize: '18px' }}>
                                        {courseDetails.absentCount}
                                      </Text>
                                      <br />
                                      <Text type="secondary" style={{ fontSize: '11px' }}>Absent</Text>
                                    </div>
                                  </Col>
                                  <Col xs={12}>
                                    <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#f0f8ff', borderRadius: '6px' }}>
                                      <Text strong style={{ color: '#1890ff', fontSize: '18px' }}>
                                        {courseDetails.upcomingClasses}
                                      </Text>
                                      <br />
                                      <Text type="secondary" style={{ fontSize: '11px' }}>Upcoming</Text>
                                    </div>
                                  </Col>
                                  <Col xs={12}>
                                    <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#f9f0ff', borderRadius: '6px' }}>
                                      <Text strong style={{ color: '#722ed1', fontSize: '18px' }}>
                                        {courseDetails.attendanceRate}%
                                      </Text>
                                      <br />
                                      <Text type="secondary" style={{ fontSize: '11px' }}>Attendance</Text>
                                    </div>
                                  </Col>
                                </Row>
              
                                {/* Quick Actions */}
                                <div style={{ marginTop: '12px', textAlign: 'center' }}>
                                  <Text type="secondary" style={{ fontSize: '10px' }}>
                                    {courseDetails.completedClasses} of {courseDetails.totalClasses} classes completed
                                  </Text>
                                </div>
                              </Card>
                            </Col>
                          );
                        })}
                      </Row>
                    </Card>
                  </Col>
              
                  
                </Row>
              </TabPane>

              {/* Subjects Tab */}
               <TabPane 
                              tab={
                                <span>
                                  {t('Courses') || 'Courses'}
                                </span>
                              } 
                              key="subjects"
                            >
                              <Row gutter={[24, 24]}>
                                {registrations.map((registration) => {
                                  const classStats = calculateClassStats(registration);
                                  
                                  return (
                                    <Col span={24} key={registration.groupid}>
                  <Card 
                    title={
                      <Space>
                        <Avatar style={{ backgroundColor: '#1890ff' }}>
                          {registration.class_code.charAt(0)}
                        </Avatar>
                        <div>
                          <Title level={4} style={{ margin: 0 }}>
                            {registration.class_code}
                          </Title>
                          <Text type="secondary">
                            {registration.subjects?.[0]?.title || 'No Subject'}
                          </Text>
                        </div>
                      </Space>
                    }
                    extra={
                      <div style={{ textAlign: 'right' }}>
                        <Text strong style={{ fontSize: 24, color: '#1890ff' }}>
                          {classStats.attendanceRate}%
                        </Text>
                        <br />
                        <Text type="secondary">Attendance Rate</Text>
                      </div>
                    }
                  >
                    {/* Hours Summary Section - Add this after the title */}
                    <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                      <Row gutter={[16, 8]}>
                        <Col xs={24} sm={8}>
                          <div style={{ textAlign: 'center' }}>
                            <Text strong style={{ fontSize: '18px', color: '#1890ff' }}>
                              {formatMinutesToHours(calculateCourseHours(registration.schedule).total)}
                            </Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: '12px' }}>Total Hours</Text>
                          </div>
                        </Col>
                        <Col xs={24} sm={8}>
                          <div style={{ textAlign: 'center' }}>
                            <Text strong style={{ fontSize: '18px', color: '#52c41a' }}>
                              {formatMinutesToHours(calculateCourseHours(registration.schedule).finished)}
                            </Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: '12px' }}>Completed Hours</Text>
                          </div>
                        </Col>
                        <Col xs={24} sm={8}>
                          <div style={{ textAlign: 'center' }}>
                            <Text strong style={{ fontSize: '18px', color: '#722ed1' }}>
                              {formatMinutesToHours(calculateCourseHours(registration.schedule).upcoming)}
                            </Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: '12px' }}>Upcoming Hours</Text>
                          </div>
                        </Col>
                      </Row>
                      
                      {/* Progress bar */}
                      <div style={{ marginTop: '12px' }}>
                        <div style={{ 
                          height: '6px', 
                          backgroundColor: '#f0f0f0', 
                          borderRadius: '3px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            height: '100%',
                            width: `${calculateCourseHours(registration.schedule).total > 0 
                              ? (calculateCourseHours(registration.schedule).finished / calculateCourseHours(registration.schedule).total) * 100 
                              : 0}%`,
                            backgroundColor: '#52c41a',
                            transition: 'width 0.3s ease'
                          }} />
                        </div>
                        <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                          Progress: {calculateCourseHours(registration.schedule).total > 0 
                            ? Math.round((calculateCourseHours(registration.schedule).finished / calculateCourseHours(registration.schedule).total) * 100)
                            : 0}% Complete
                        </Text>
                      </div>
                    </div>
              
                    {/* Existing attendance statistics */}
                    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                      <Col xs={24} md={8}>
                        <Card size="small" style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}>
                          <Statistic
                            title="Present"
                            value={registration.schedule?.filter(cls => {
                              const attendanceLog = getAttendanceForClass(cls.classid);
                              return attendanceLog?.status === 'Present';
                            }).length || 0}
                            prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                            valueStyle={{ color: '#52c41a' }}
                          />
                        </Card>
                      </Col>
                      
                      <Col xs={24} md={8}>
                        <Card size="small" style={{ backgroundColor: '#fffbe6', border: '1px solid #ffe58f' }}>
                          <Statistic
                            title="Late"
                            value={registration.schedule?.filter(cls => {
                              const attendanceLog = getAttendanceForClass(cls.classid);
                              return attendanceLog?.status?.startsWith('Late');
                            }).length || 0}
                            prefix={<ExclamationCircleOutlined style={{ color: '#faad14' }} />}
                            valueStyle={{ color: '#faad14' }}
                          />
                        </Card>
                      </Col>
                      
                      <Col xs={24} md={8}>
                        <Card size="small" style={{ backgroundColor: '#fff2f0', border: '1px solid #ffccc7' }}>
                          <Statistic
                            title="Absent"
                            value={registration.schedule?.filter(cls => {
                              const attendanceLog = getAttendanceForClass(cls.classid);
                              return attendanceLog?.status === 'Absent';
                            }).length || 0}
                            prefix={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                            valueStyle={{ color: '#ff4d4f' }}
                          />
                        </Card>
                      </Col>
                    </Row>
              
                    {/* Rest of your existing schedule cards... */}
                    <Row gutter={[16, 16]}>
                      {registration.schedule
                        ?.sort((a, b) => new Date(a.date) - new Date(b.date))
                        .map((cls, index) => {
                          const attendanceLog = getAttendanceForClass(cls.classid);
                          const status = attendanceLog ? attendanceLog.status : 'No Attendance';
                          const statusConfig = getStatusConfig(status);
                          
                          // Add class status indicator (past/upcoming)
                          const classDate = new Date(cls.date);
                          const today = new Date();
                          const isUpcoming = classDate >= today;
                          
                          return (
                            <Col xs={24} sm={12} md={8} key={index}>
                              <Card 
                                size="small"
                                style={{ 
                                  borderLeft: `4px solid ${
                                    status === 'Present' ? '#52c41a' : 
                                    status?.startsWith('Late') ? '#faad14' : 
                                    status === 'Absent' ? '#ff4d4f' : '#d9d9d9'
                                  }`,
                                  opacity: isUpcoming ? 1 : 0.8
                                }}
                              >
                                <div style={{ marginBottom: 8 }}>
                                  <Text strong>{cls.subject}</Text>
                                  <div style={{ float: 'right' }}>
                                    {isUpcoming && (
                                      <Tag color="blue" size="small" style={{ marginRight: 4 }}>
                                        Upcoming
                                      </Tag>
                                    )}
                                    <Tag 
                                      color={statusConfig.color} 
                                      icon={<statusConfig.icon />}
                                      size="small"
                                    >
                                      {statusConfig.text}
                                    </Tag>
                                  </div>
                                </div>
                                
                                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                  <Space>
                                    <CalendarOutlined />
                                    <Text type="secondary">{formatDate(cls.date)}</Text>
                                  </Space>
                                  <Space>
                                    <ClockCircleOutlined />
                                    <Text type="secondary">
                                      {formatTime(cls.time)} - {calculateEndTime(cls.time, cls.hours)}
                                    </Text>
                                    <Tag size="small" color="purple">
                                      {cls.hours}
                                    </Tag>
                                  </Space>
                                  <Space>
                                    <UserOutlined />
                                    <Text type="secondary">{cls.teacher || 'No Teacher'}</Text>
                                  </Space>
                                  {cls.classroom && (
                                    <Space>
                                      <BookOutlined />
                                      <Text type="secondary">Room {cls.classroom}</Text>
                                    </Space>
                                  )}
                                  {attendanceLog?.homework && (
                                    <Tag 
                                      color={attendanceLog.homework === 'YES' ? 'green' : 'red'}
                                      style={{ marginTop: 4 }}
                                    >
                                      HW: {attendanceLog.homework}
                                    </Tag>
                                  )}
                                  {attendanceLog?.comment && (
                                    <Alert
                                      message={attendanceLog.comment}
                                      type={
                                        status === 'Present' ? 'success' :
                                        status?.startsWith('Late') ? 'warning' :
                                        status === 'Absent' ? 'error' : 'info'
                                      }
                                      showIcon
                                      style={{ marginTop: 8 }}
                                    />
                                  )}
                                </Space>
                              </Card>
                            </Col>
                          );
                        }) || []}
                    </Row>
                  </Card>
                </Col>
                                  );
                                })}
                              </Row>
                            </TabPane>

              {/* Timeline Tab */}
              <TabPane 
                tab={
                  <span>
                    {t('Timeline') || 'Timeline'}
                  </span>
                } 
                key="timeline"
              >
                <Card title={t('Activity Timeline') || 'Activity Timeline'}>
                  <List
                    itemLayout="horizontal"
                    dataSource={registrations
                      .flatMap(reg => 
                        (reg.schedule || []).map(cls => ({
                          ...cls,
                          class_code: reg.class_code,
                          attendanceLog: getAttendanceForClass(cls.classid)
                        }))
                      )
                      .sort((a, b) => new Date(a.date) - new Date(b.date))}
                    renderItem={(cls) => {
                      const status = cls.attendanceLog ? cls.attendanceLog.status : 'No Attendance';
                      const statusConfig = getStatusConfig(status);
                      
                      return (
                        <List.Item>
                          <List.Item.Meta
                            avatar={
                              <Avatar 
                                style={{ 
                                  backgroundColor: 
                                    status === 'Present' ? '#52c41a' : 
                                    status?.startsWith('Late') ? '#faad14' : 
                                    status === 'Absent' ? '#ff4d4f' : '#d9d9d9'
                                }}
                                icon={<statusConfig.icon />}
                              />
                            }
                            title={
                              <Space>
                                <Text strong>{cls.class_code} - {cls.subject}</Text>
                                <Tag color={statusConfig.color}>{statusConfig.text}</Tag>
                              </Space>
                            }
                            description={
                              <Space direction="vertical" size="small">
                                <Text type="secondary">
                                  {formatDate(cls.date)} • {formatTime(cls.time)} - {calculateEndTime(cls.time, cls.hours)}
                                </Text>
                                <Text type="secondary">Teacher: {cls.teacher || 'No Teacher'}</Text>
                                {cls.classroom && (
                                  <Text type="secondary">Room: {cls.classroom}</Text>
                                )}
                                {cls.attendanceLog?.homework && (
                                  <Tag color={cls.attendanceLog.homework === 'YES' ? 'green' : 'red'}>
                                    Homework: {cls.attendanceLog.homework}
                                  </Tag>
                                )}
                              </Space>
                            }
                          />
                        </List.Item>
                      );
                    }}
                  />
                </Card>
              </TabPane>
            </Tabs>
          </Card>
        </Content>
      </Layout>
    </Layout>
  );
};

export default withRoleAccess(MyPerformance, ['student']);